import type { FC } from 'react';

import ErrorSection from 'components/ErrorSection/ErrorSection';
import Popup from 'components/Popup/Popup';
import SuccessSection from 'components/SuccessSection/SuccessSection';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import type { NoopT } from 'declarations/types/common';
import useTicketReopenApprovalPopup from 'pages/TicketsPage/components/TicketDetailsPopup/hooks/useTicketDetailsPopup';

import {
  ActionBtnStyled,
  ControlsWrapStyled,
  FlexContainerMessageStyled,
  getPopupContentStyle,
} from './DeleteTicketPopup.styled';
import useDeleteTicketPopup from './hooks/useDeleteTicketPopup';

type DeleteTicketPopupPropsT = {
  ticketId: string;
  isOpen: boolean;
  onClose: NoopT;
};

const DeleteTicketPopup: FC<DeleteTicketPopupPropsT> = ({ ticketId, isOpen, onClose }) => {
  const { isMobile } = useResponsive();
  const { isLoadingTicketDetails, isTicketDetailsFetchingError } = useTicketReopenApprovalPopup();
  const {
    isDeletingTicket,
    isTicketDeleted,
    isTicketDeletedError,
    deleteTicketErrorMessage,
    deleteTicketValidationErrorMessage,
    showPopupContent,
    handleCloseDeletePopup,
    handleClosePopup,
  } = useDeleteTicketPopup();

  return (
    <Popup
      nested
      popupId={ScrollLockIdE.TicketDetailsPopup}
      contentStyle={getPopupContentStyle(isMobile)}
      isOpen={isOpen}
      onClose={handleClosePopup(onClose)}
      showCloseBtn={isLoadingTicketDetails || !!isTicketDetailsFetchingError}
      closeOnEscape={false}
    >
      {showPopupContent && (
        <>
          <Typography color="primaryText" variant="h2" align={isMobile ? 'center' : 'left'}>
            Are you sure you want to delete the ticket?
          </Typography>
          <ControlsWrapStyled justify={isMobile ? 'center' : 'flex-end'}>
            <ActionBtnStyled
              type="button"
              variant="outline"
              size="xl"
              onClick={handleClosePopup(onClose)}
            >
              No
            </ActionBtnStyled>
            <ActionBtnStyled
              variant="contained"
              size="xl"
              isLoading={isDeletingTicket}
              onClick={handleCloseDeletePopup({ ticketId, onClose })}
            >
              Yes
            </ActionBtnStyled>
          </ControlsWrapStyled>
        </>
      )}

      {isTicketDeleted && (
        <FlexContainerMessageStyled direction="column">
          <SuccessSection message="Ticket successfully deleted" />
        </FlexContainerMessageStyled>
      )}

      {isTicketDeletedError && (
        <FlexContainerMessageStyled direction="column">
          <ErrorSection
            errorDetails={deleteTicketErrorMessage || deleteTicketValidationErrorMessage}
          />
        </FlexContainerMessageStyled>
      )}
    </Popup>
  );
};

export default DeleteTicketPopup;
